import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";

import { startMfaEnrollment, verifyMfaEnrollment } from "../../auth/cognito";

// Forced MFA enrolment page for admins. Reached when /auth/login
// returns state=mfa_enrollment_required — the user is technically
// password-authenticated but holds no ID token, no refresh cookie.
// They cannot navigate elsewhere in the SPA until they complete this
// flow because there's no session for the route guards to accept.
//
// Flow:
//   1. Page mounts → POST /auth/mfa/enroll/start with the access
//      token from location.state → receives the TOTP secret.
//   2. SPA renders the secret as a QR code (otpauth://) for the
//      user's authenticator app.
//   3. User enters the first 6-digit code → POST /auth/mfa/enroll/
//      verify → server marks MFA enabled + preferred for this user.
//   4. SPA navigates to /sign-in with a friendly banner asking them
//      to sign in again. Cognito will challenge them for a TOTP
//      code on every subsequent sign-in.

interface EnrollmentState {
  accessToken: string;
  email: string;
}

export function MfaEnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const enrollment = location.state as EnrollmentState | undefined;

  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(true);

  // No state in location → user reached this page by typing the URL,
  // bounce them back to /sign-in.
  useEffect(() => {
    if (!enrollment?.accessToken) navigate("/sign-in", { replace: true });
  }, [enrollment, navigate]);

  // Mount → call /auth/mfa/enroll/start to get the TOTP secret.
  useEffect(() => {
    if (!enrollment?.accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { secretCode } = await startMfaEnrollment(enrollment.accessToken);
        if (cancelled) return;
        setSecret(secretCode);
        const issuer = "RIoT-FOTA";
        const label = encodeURIComponent(`${issuer}:${enrollment.email}`);
        const otpauth = `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
        const dataUrl = await QRCode.toDataURL(otpauth, { margin: 1, width: 200, errorCorrectionLevel: "M" });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not start MFA enrolment");
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enrollment]);

  if (!enrollment) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    setError(null);
    setLoading(true);
    try {
      await verifyMfaEnrollment({
        accessToken: enrollment.accessToken,
        email: enrollment.email,
        code,
      });
      navigate("/sign-in", {
        replace: true,
        state: { signInError: "MFA enabled — please sign in again with your authenticator code." },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "MFA verification failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-riot-navy-950 px-6 py-10">
      <div className="w-full max-w-md rounded-[28px] border border-white/12 bg-riot-navy-900/80 px-8 py-10 shadow-[0_28px_90px_rgba(2,8,23,0.55)] backdrop-blur-md">
        <h1 className="mb-2 text-2xl font-bold text-white">Set up two-factor authentication</h1>
        <p className="mb-6 text-sm text-riot-navy-200/80">
          Admin accounts on RIoT-FOTA require a second factor. Scan the QR code below with
          your authenticator app (Google Authenticator, 1Password, Authy, etc.) and enter the
          6-digit code it shows to finish setup.
        </p>

        {starting && <p className="text-sm text-riot-navy-300">Generating secret…</p>}

        {qrDataUrl && (
          <div className="mb-4 flex justify-center rounded-xl bg-white p-4">
            <img src={qrDataUrl} alt="MFA setup QR code" width={200} height={200} />
          </div>
        )}
        {secret && (
          <div className="mb-4 text-center text-[11px] text-riot-navy-300">
            Or enter this secret manually:&nbsp;
            <code className="rounded bg-riot-navy-800 px-2 py-0.5 text-riot-navy-100">{secret}</code>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="enroll-code" className="mb-1 block text-xs font-medium text-riot-navy-100/90">
              6-digit code
            </label>
            <input
              id="enroll-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              placeholder="000000"
              className="w-full rounded-xl border border-white/10 bg-white/6 px-3 py-2.5 text-center font-mono text-lg tracking-[0.4em] text-white placeholder:text-riot-navy-300/70 focus:border-riot-gold-400/70 focus:outline-none focus:ring-2 focus:ring-riot-gold-400/30"
            />
          </div>

          {error && (
            <div role="alert" className="rounded-xl border border-red-500/25 bg-red-950/35 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || code.length !== 6 || !secret}
            className="w-full rounded-xl bg-riot-gold-400 px-4 py-2.5 text-sm font-semibold text-riot-navy-950 transition hover:bg-riot-gold-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Verify & finish setup"}
          </button>
        </form>
      </div>
    </div>
  );
}
